import { GridOptions, ValueFormatterParams } from "ag-grid-community";
import { ColDef } from "ag-grid-community/dist/lib/entities/colDef";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgTableWrapper, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { MetricArrayType } from "@intelligems/ig-types";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";

import { useConfiguration } from "../../../../api/configurations/use-configuration";
import { noDecimalNumberFormatter } from "../../../../api/formatters/dashboard";
import { getDrawer, setDrawer } from "../../../../store/slices/global";
import theme from "../../../../theme";
import { removeNumberAndHyphen } from "../../Utils/utils";

import { OrderExampleDrawer } from "./OrderExampleDrawer";
import { reasonDescriptionMapping } from "./utils";

interface DataReconciliationTableProps {
  data?: MetricArrayType;
}
export const DataReconciliationTable = ({
  data,
}: DataReconciliationTableProps) => {
  const dispatch = useDispatch();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const orderExamplesDrawer = useSelector(getDrawer("orderExamples"));

  const gridRef = useRef<AgGridReact>(null);
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 50,
      resizable: false,
      cellStyle: { fontSize: "12px" },
      menuTabs: [],
      suppressMovable: true,
    };
  }, []);

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: "Reason",
        headerClass: "header-custom",
        field: "EXCLUSION_REASON_DESCRIPTION",
        cellStyle: {
          justifyContent: "flex-start",
        },
        flex: 0.6,
        valueFormatter: (params: ValueFormatterParams) => {
          return capitalizeFirstLetter(removeNumberAndHyphen(params.value));
        },
        cellRenderer: (params) => {
          return (
            <IgTypography
              sx={{ fontSize: "12px", letterSpacing: ".2px", wordSpacing: 0 }}
            >
              {params.valueFormatted}
            </IgTypography>
          );
        },
      },
      {
        headerName: "Number of Orders",
        headerClass: "header-custom",
        field: "N_ORDERS",
        flex: 0.4,
        cellStyle: {
          justifyContent: "flex-start",
        },
        cellRenderer: (params) => {
          return (
            <IgTypography
              sx={{ fontSize: "12px", letterSpacing: ".2px", wordSpacing: 0 }}
            >
              {params.valueFormatted}
            </IgTypography>
          );
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return noDecimalNumberFormatter(
            params.value,
            false,
            storeCurrencyConfig?.value
          );
        },
      },
      {
        headerName: "Description",
        headerClass: "header-custom",
        field: "mappedDescription",
        flex: 1,
        cellStyle: {
          justifyContent: "flex-start",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return reasonDescriptionMapping[
            params.data["EXCLUSION_REASON_DESCRIPTION"]
          ];
        },
        cellRenderer: (params) => {
          return (
            <IgTypography
              sx={{
                fontSize: "12px",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                letterSpacing: ".2px",
                wordSpacing: 0,
              }}
            >
              <IgTooltip title={params.valueFormatted}>
                {params.valueFormatted}
              </IgTooltip>
            </IgTypography>
          );
        },
      },
      {
        headerName: "Example Orders",
        headerClass: "header-custom",
        flex: 0.4,
        field: "ORDER_IDS",
        cellRenderer: (params) => {
          return (
            <IgTypography
              onClick={() => {
                dispatch(
                  setDrawer({
                    drawer: "orderExamples",
                    open: true,
                    props: {
                      data: params.data,
                    },
                  })
                );
              }}
              sx={{
                fontSize: "12px",
                letterSpacing: ".2px",
                wordSpacing: 0,
                color: theme.palette.primary.main,
                "&:hover": {
                  cursor: "pointer",
                  color: theme.palette.primary.dark,
                },
              }}
            >
              See Orders
            </IgTypography>
          );
        },
        cellStyle: {
          justifyContent: "flex-start",
        },
      },
    ];
  }, [data]);

  const loadingCellRendererParams = useMemo(() => {
    return {
      loadingMessage: "One moment please...",
    };
  }, []);

  const gridOptions = useMemo<GridOptions>(() => {
    return {
      rowData: data,
    };
  }, []);

  return (
    <Grid item container direction={"column"} rowSpacing={2}>
      {/*<Grid item>*/}
      {/*  <IgTextField*/}
      {/*    id={"filter-text-box"}*/}
      {/*    fullWidth={true}*/}
      {/*    sx={{*/}
      {/*      backgroundColor: "white",*/}
      {/*    }}*/}
      {/*    placeholder={"Search By Order ID"}*/}
      {/*    onInput={onFilterTextBoxChanged}*/}
      {/*    InputProps={{*/}
      {/*      endAdornment: (*/}
      {/*        <InputAdornment position="end">*/}
      {/*          <Search fontSize={"small"} sx={{ color: "#909090" }} />*/}
      {/*        </InputAdornment>*/}
      {/*      ),*/}
      {/*    }}*/}
      {/*  />*/}
      {/*</Grid>*/}
      <Grid item>
        <IgTableWrapper sx={{ minHeight: "100px" }}>
          <AgGridReact
            ref={gridRef}
            defaultColDef={defaultColDef}
            rowData={data}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            suppressDragLeaveHidesColumns={true}
            suppressExcelExport={true}
            suppressCsvExport={true}
            overlayLoadingTemplate={
              "<div>Loading Exclusion Order Data ... </div>"
            }
            loadingCellRendererParams={loadingCellRendererParams}
          />
        </IgTableWrapper>
      </Grid>
      {orderExamplesDrawer?.props && <OrderExampleDrawer />}
    </Grid>
  );
};
